/**
 * Bookings Management Page
 * ========================
 * 
 * Calendar view and booking management for merchants.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  ArrowLeft,
  Check,
  X,
  AlertCircle,
  Loader2,
  CalendarDays,
  List,
  Settings,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  confirmed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  in_progress: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  no_show: "bg-red-500/20 text-red-400 border-red-500/30",
};

const BOOKING_STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled", "no_show"];

const BookingsPage = () => {
  const { token } = useAuth();
  
  // Data state
  const [bookings, setBookings] = useState({});
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View state
  const [viewMode, setViewMode] = useState("week"); // day, week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState("all");
  
  // Modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    service_id: "",
    staff_id: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    booking_date: "",
    start_time: "",
    notes: "",
    deposit_paid: false,
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (viewMode === "day") {
      // Just current day
    } else if (viewMode === "week") {
      start.setDate(start.getDate() - start.getDay() + 1); // Monday
      end.setDate(start.getDate() + 6); // Sunday
    } else {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }
    
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
      dates: getDatesInRange(start, end),
    };
  }, [currentDate, viewMode]);

  function getDatesInRange(start, end) {
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  // Fetch data
  const fetchBookings = useCallback(async () => {
    try {
      let url = `${API}/bookings/calendar?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      if (selectedStaff && selectedStaff !== "all") {
        url += `&staff_id=${selectedStaff}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setBookings(data);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange.start, dateRange.end, selectedStaff]);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/services?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setServices(data);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await fetch(`${API}/pos/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchAvailableSlots = async (serviceId, staffId, date) => {
    if (!serviceId || !staffId || !date) {
      setAvailableSlots([]);
      return;
    }
    
    try {
      const res = await fetch(
        `${API}/bookings/availability/${serviceId}?date=${date}&staff_id=${staffId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        const slots = data[staffId]?.slots || [];
        setAvailableSlots(slots);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
    }
  };

  useEffect(() => {
    fetchServices();
    fetchStaff();
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (formData.service_id && formData.staff_id && formData.booking_date) {
      fetchAvailableSlots(formData.service_id, formData.staff_id, formData.booking_date);
    }
  }, [formData.service_id, formData.staff_id, formData.booking_date, token]);

  // Navigation
  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Form handlers
  const resetForm = () => {
    setFormData({
      service_id: "",
      staff_id: "",
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      booking_date: "",
      start_time: "",
      notes: "",
      deposit_paid: false,
    });
    setAvailableSlots([]);
    setSelectedSlot(null);
  };

  const openCreateModal = (date = null) => {
    resetForm();
    if (date) {
      setFormData((prev) => ({ ...prev, booking_date: date }));
    }
    setShowBookingModal(true);
  };

  const openDetailModal = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.service_id || !formData.staff_id || !formData.booking_date || !formData.start_time) {
      toast.error("Please fill all required fields");
      return;
    }
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Customer details are required");
      return;
    }
    
    setSaving(true);
    
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (res.ok) {
        toast.success("Booking created");
        setShowBookingModal(false);
        resetForm();
        fetchBookings();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create booking");
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error("Failed to create booking");
    } finally {
      setSaving(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API}/bookings/${bookingId}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      
      if (res.ok) {
        toast.success(`Booking ${status}`);
        setShowDetailModal(false);
        fetchBookings();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Format helpers
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const formatDateHeader = () => {
    if (viewMode === "day") {
      return currentDate.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } else if (viewMode === "week") {
      const start = new Date(dateRange.start);
      const end = new Date(dateRange.end);
      return `${start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
    } else {
      return currentDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    }
  };

  // Time slots for day view
  const timeSlots = [];
  for (let h = 8; h <= 18; h++) {
    timeSlots.push(`${h.toString().padStart(2, "0")}:00`);
  }

  // Get bookings for a specific date
  const getBookingsForDate = (date) => bookings[date] || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header - Brand Gradient */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant/services" className="p-2 rounded-lg hover:bg-white/10 text-indigo-100 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">Bookings</h1>
              <p className="text-sm text-indigo-100">Manage appointments</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedStaff} onValueChange={setSelectedStaff}>
              <SelectTrigger className="w-40 bg-white/20 border-0 text-white">
                <SelectValue placeholder="All Staff" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.staff_id} value={s.staff_id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={() => openCreateModal()} className="bg-white text-indigo-700 hover:bg-indigo-50" data-testid="create-booking-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Booking
            </Button>
          </div>
        </div>
      </header>

      {/* Calendar Controls */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="border-gray-200 text-gray-700 hover:bg-gray-100">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="border-gray-200 text-gray-700 hover:bg-gray-100">
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(1)} className="border-gray-200 text-gray-700 hover:bg-gray-100">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold ml-4 text-gray-900">{formatDateHeader()}</h2>
          </div>
          
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === "day" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("day")}
              className={viewMode === "day" ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-200"}
            >
              Day
            </Button>
            <Button
              variant={viewMode === "week" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("week")}
              className={viewMode === "week" ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-200"}
            >
              Week
            </Button>
            <Button
              variant={viewMode === "month" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("month")}
              className={viewMode === "month" ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-200"}
            >
              Month
            </Button>
          </div>
        </div>

        {/* Calendar View */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : viewMode === "week" ? (
          /* Week View */
          <div className="grid grid-cols-7 gap-2">
            {dateRange.dates.map((date) => {
              const dateObj = new Date(date);
              const isToday = date === new Date().toISOString().split("T")[0];
              const dayBookings = getBookingsForDate(date);
              
              return (
                <div
                  key={date}
                  className={`min-h-[300px] rounded-xl border ${isToday ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white"} shadow-sm`}
                >
                  <div
                    className={`p-3 border-b ${isToday ? "border-indigo-200" : "border-gray-100"} cursor-pointer hover:bg-gray-50`}
                    onClick={() => openCreateModal(date)}
                  >
                    <p className="text-xs text-gray-500">
                      {dateObj.toLocaleDateString("en-GB", { weekday: "short" })}
                    </p>
                    <p className={`text-lg font-semibold ${isToday ? "text-indigo-600" : "text-gray-900"}`}>
                      {dateObj.getDate()}
                    </p>
                  </div>
                  
                  <div className="p-2 space-y-1.5 max-h-[240px] overflow-y-auto">
                    {dayBookings.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">No bookings</p>
                    ) : (
                      dayBookings.map((booking) => (
                        <button
                          key={booking.booking_id}
                          onClick={() => openDetailModal(booking)}
                          className={`w-full text-left p-2 rounded-lg border text-xs transition-all hover:scale-[1.02] ${STATUS_COLORS[booking.status]}`}
                          data-testid={`booking-${booking.booking_id}`}
                        >
                          <p className="font-medium truncate">{booking.start_time} - {booking.service_name}</p>
                          <p className="text-gray-600 truncate">{booking.customer_name}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === "day" ? (
          /* Day View */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {timeSlots.map((time) => {
              const todayStr = currentDate.toISOString().split("T")[0];
              const dayBookings = getBookingsForDate(todayStr);
              const slotBookings = dayBookings.filter((b) => b.start_time <= time && b.end_time > time);
              
              return (
                <div key={time} className="flex border-b border-gray-100 last:border-0">
                  <div className="w-20 p-3 text-sm text-gray-500 border-r border-gray-100 flex-shrink-0">
                    {time}
                  </div>
                  <div className="flex-1 p-2 min-h-[60px]">
                    {slotBookings.map((booking) => (
                      <button
                        key={booking.booking_id}
                        onClick={() => openDetailModal(booking)}
                        className={`w-full text-left p-2 rounded-lg border mb-1 ${STATUS_COLORS[booking.status]}`}
                      >
                        <p className="font-medium text-sm">{booking.service_name}</p>
                        <p className="text-xs">{booking.customer_name} • {booking.staff_name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Month View */
          <div className="grid grid-cols-7 gap-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <div key={day} className="p-2 text-center text-sm text-gray-500 font-medium">
                {day}
              </div>
            ))}
            {dateRange.dates.map((date) => {
              const dateObj = new Date(date);
              const isToday = date === new Date().toISOString().split("T")[0];
              const dayBookings = getBookingsForDate(date);
              
              return (
                <div
                  key={date}
                  className={`min-h-[80px] p-1 rounded-lg border cursor-pointer hover:bg-gray-50 ${isToday ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white"}`}
                  onClick={() => {
                    setCurrentDate(dateObj);
                    setViewMode("day");
                  }}
                >
                  <p className={`text-xs font-medium mb-1 ${isToday ? "text-indigo-600" : "text-gray-500"}`}>
                    {dateObj.getDate()}
                  </p>
                  {dayBookings.length > 0 && (
                    <div className="space-y-0.5">
                      {dayBookings.slice(0, 2).map((b) => (
                        <div
                          key={b.booking_id}
                          className={`text-[10px] px-1 py-0.5 rounded truncate ${STATUS_COLORS[b.status]}`}
                        >
                          {b.start_time}
                        </div>
                      ))}
                      {dayBookings.length > 2 && (
                        <p className="text-[10px] text-gray-400">+{dayBookings.length - 2} more</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Booking</DialogTitle>
            <DialogDescription className="text-gray-500">
              Schedule a new appointment
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 block mb-2">Service *</label>
              <Select
                value={formData.service_id}
                onValueChange={(v) => {
                  const service = services.find((s) => s.service_id === v);
                  setFormData((prev) => ({
                    ...prev,
                    service_id: v,
                    staff_id: service?.staff_ids?.[0] || "",
                  }));
                }}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {services.map((s) => (
                    <SelectItem key={s.service_id} value={s.service_id}>
                      {s.name} ({s.duration_minutes}min - {formatPrice(s.base_price)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.service_id && (
              <div>
                <label className="text-sm text-gray-600 block mb-2">Staff *</label>
                <Select
                  value={formData.staff_id}
                  onValueChange={(v) => setFormData({ ...formData, staff_id: v })}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {services
                      .find((s) => s.service_id === formData.service_id)
                      ?.staff?.map((s) => (
                        <SelectItem key={s.staff_id} value={s.staff_id}>
                          {s.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 block mb-2">Date *</label>
                <Input
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                  className="bg-gray-50 border-gray-200"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 block mb-2">Time *</label>
                <Select
                  value={formData.start_time}
                  onValueChange={(v) => setFormData({ ...formData, start_time: v })}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-48">
                    {availableSlots.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No slots available
                      </SelectItem>
                    ) : (
                      availableSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium mb-3 text-gray-900">Customer Details</h4>
              <div className="space-y-3">
                <Input
                  placeholder="Customer Name *"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="bg-gray-50 border-gray-200"
                  data-testid="booking-customer-name"
                />
                <Input
                  type="email"
                  placeholder="Email *"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="bg-gray-50 border-gray-200"
                />
                <Input
                  type="tel"
                  placeholder="Phone *"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  className="bg-gray-50 border-gray-200"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 block mb-2">Notes</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-gray-50 border-gray-200"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)} className="border-gray-200 text-gray-700">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white"
              data-testid="save-booking-btn"
            >
              {saving ? "Creating..." : "Create Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedBooking?.booking_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg text-gray-900">{selectedBooking.service_name}</h3>
                <Badge className={STATUS_COLORS[selectedBooking.status]}>
                  {selectedBooking.status.replace("_", " ")}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{selectedBooking.booking_date}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{selectedBooking.start_time} - {selectedBooking.end_time}</span>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Customer</p>
                <p className="font-medium text-gray-900">{selectedBooking.customer_name}</p>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {selectedBooking.customer_email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {selectedBooking.customer_phone}
                  </span>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Payment</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Deposit ({selectedBooking.deposit_percent}%)</span>
                  <span className={selectedBooking.deposit_paid ? "text-emerald-600" : "text-amber-600"}>
                    {formatPrice(selectedBooking.deposit_amount)} {selectedBooking.deposit_paid ? "✓" : "(pending)"}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-700">Balance</span>
                  <span className="text-gray-900">{formatPrice(selectedBooking.final_amount)}</span>
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t border-gray-200">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">{formatPrice(selectedBooking.total_amount)}</span>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Staff</p>
                <p className="font-medium text-gray-900">{selectedBooking.staff_name}</p>
              </div>
              
              {selectedBooking.notes && (
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Notes</p>
                  <p className="text-sm text-gray-700">{selectedBooking.notes}</p>
                </div>
              )}
              
              {/* Status Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {selectedBooking.status === "pending" && (
                  <Button
                    size="sm"
                    onClick={() => updateBookingStatus(selectedBooking.booking_id, "confirmed")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Confirm
                  </Button>
                )}
                {selectedBooking.status === "confirmed" && (
                  <Button
                    size="sm"
                    onClick={() => updateBookingStatus(selectedBooking.booking_id, "in_progress")}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Start Service
                  </Button>
                )}
                {selectedBooking.status === "in_progress" && (
                  <Button
                    size="sm"
                    onClick={() => updateBookingStatus(selectedBooking.booking_id, "completed")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Complete
                  </Button>
                )}
                {["pending", "confirmed"].includes(selectedBooking.status) && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateBookingStatus(selectedBooking.booking_id, "no_show")}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      No-Show
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateBookingStatus(selectedBooking.booking_id, "cancelled")}
                      className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;
